import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Crown, Gavel, Loader2, Music4 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/auth/users").then(({ data }) => setUsers(data)).catch(() => {});
  }, []);

  const doLogin = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    try {
      await login(selected.name, password);
      toast.success(`Welcome, ${selected.name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#070B14] flex flex-col items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute inset-0 opacity-25 pointer-events-none"
           style={{ backgroundImage: "radial-gradient(circle at 50% 0%, rgba(212,175,55,0.25), transparent 55%)" }} />

      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 mb-5">
            <Music4 className="w-4 h-4 text-amber-400" />
            <span className="text-amber-300 text-xs font-semibold tracking-widest">KWC LUZON · SEP 27, 2026</span>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight text-amber-100 text-glow">
            Judges Scoring
          </h1>
          <p className="text-slate-400 mt-3">Select your name to sign in</p>
        </div>

        <form onSubmit={doLogin} className="card-kwc p-5 sm:p-6 space-y-5">
          <div className="grid grid-cols-1 gap-2.5" data-testid="user-select-list">
            {users.map((u) => {
              const active = selected?.name === u.name;
              return (
                <button
                  key={u.name}
                  type="button"
                  data-testid={`user-option-${u.name.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => setSelected(u)}
                  className={`flex items-center gap-3 w-full text-left px-4 h-14 rounded-xl border transition-colors ${
                    active
                      ? "bg-amber-500/15 border-amber-500 text-amber-100"
                      : "bg-slate-900 border-slate-700 text-slate-200 hover:border-amber-500/40"
                  }`}
                >
                  {u.is_superadmin ? <Crown className="w-5 h-5 text-amber-400" /> : <Gavel className="w-5 h-5 text-slate-400" />}
                  <span className="flex-1 font-semibold">{u.name}</span>
                  <span className="text-[11px] uppercase tracking-wide text-slate-400">
                    {u.is_superadmin ? "SuperAdmin" : "Judge"}
                  </span>
                </button>
              );
            })}
          </div>

          <div>
            <label className="block text-sm text-slate-400 mb-2">Password</label>
            <input
              type="password"
              data-testid="password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full h-14 px-4 rounded-xl bg-slate-950 border border-slate-700 text-slate-100 focus:border-amber-500 focus:outline-none text-lg"
            />
          </div>

          <button
            type="submit"
            data-testid="login-button"
            disabled={!selected || !password || submitting}
            className="btn-gold w-full h-14 rounded-xl text-lg flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
            Sign In {selected ? `as ${selected.name.split(" ")[0]}` : ""}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
